import { Injectable } from '@angular/core';

import { NgxResizeObserverService } from 'ngx-resize-observer';
import { Observable, Subject } from 'rxjs';

class ResizeObserverSubject {
  public subscriptionCount = 0;

  private readonly observeResize$ = new Subject<ResizeObserverEntry>();

  constructor(private readonly resizeObserver: NgxResizeObserverService, public readonly element: HTMLElement) {}

  public observe$(): Observable<ResizeObserverEntry> {
    if (this.subscriptionCount === 0) {
      this.resizeObserver.observe(
        this.element,
        entry => {
          this.observeResize$.next(entry);
        },
        'border-box'
      );
    }
    this.subscriptionCount++;

    return this.observeResize$.asObservable();
  }

  public unobserve(): void {
    this.subscriptionCount--;
    if (this.subscriptionCount === 0) {
      this.resizeObserver.unobserve(this.element);
      this.observeResize$.complete();
    }
  }
}

/**
 * Use this service to wrap subscriptions to NgxResizeObserverService in case it's required to observe
 * resizes of same element in multiple cases. NgxResizeObserverService doesn't support multiple observations
 * of same element, so this service acts as a wrapper which holds a registry of existing observer instances,
 * which will be reused in case a new resize subscription refers to an already observed element.
 */
@Injectable()
export class ResizeObserverWrapperService {
  private observers: ResizeObserverSubject[] = [];

  constructor(private readonly resizeObserver: NgxResizeObserverService) {}

  public observe$(element: HTMLElement): Observable<ResizeObserverEntry> {
    const observer = this.observers.find(o => o.element === element) || this.createObserver(element);
    return observer.observe$();
  }

  public unobserve(element: HTMLElement): void {
    this.observers.find(o => o.element === element)?.unobserve();
    this.observers = this.observers.filter(o => o.subscriptionCount > 0);
  }

  private createObserver(element: HTMLElement): ResizeObserverSubject {
    const observer = new ResizeObserverSubject(this.resizeObserver, element);
    this.observers.push(observer);
    return observer;
  }
}
